import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'
import { CONTINENTS, LOCATIONS } from '@/lib/locations'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

const POST_URL = 'https://uptrack.app/blog/22-check-locations'
const TITLE = '24 Check Locations Without 24 Times the False Alarms'
const DESCRIPTION = MONITOR_LOCATIONS.blogDescription

const FAQ: { q: string; a: string }[] = [
  {
    q: 'Which locations does Uptrack check from?',
    a: CONTINENTS.map((c) => `${LOCATIONS.filter((l) => l.continent === c).map((l) => l.city).join(', ')} in ${c}`).join('; ') + '.',
  },
  {
    q: 'Does every location check my site on every interval?',
    a: 'No. The 24 locations form 12 areas of two nearby Cloudflare cities. In each interval one city per area checks, and the two cities take turns, so every area sees your site on every interval. The 2 core probes check on every interval.',
  },
  {
    q: 'Will one failing city send me an alert?',
    a: 'No. A failure seen by one of the added cities is re-checked at once from the same city, a failure must repeat for your number of consecutive checks (2 to 5), and several locations must confirm it before you get an alert.',
  },
  {
    q: 'What happens if my firewall or bot protection blocks one location?',
    a: 'If one city gets a block or a rate limit while the other locations see your site up, Uptrack marks that city as blocked for your site, not down. The other city of the same area checks your site in its place.',
  },
  {
    q: 'Do ping, port, DNS and SSL checks run from all 24 locations?',
    a: 'No. HTTP, HTTPS and keyword monitors use the 24 locations. Ping, TCP port, DNS and SSL checks run from the 2 core locations: Nuremberg and Hyderabad.',
  },
  {
    q: 'Which plan includes which locations?',
    a: 'Pro includes 6 areas that you pick. Team and Business include all 24.',
  },
]

export const Route = createFileRoute('/_site/blog/22-check-locations')({
  component: CheckLocationsPage,
  head: () => ({
    meta: [
      { title: '24 Check Locations Without 24x the False Alarms — Uptrack' },
      { name: 'description', content: DESCRIPTION },
      { property: 'og:title', content: '24 Check Locations Without 24x the False Alarms — Uptrack' },
      { property: 'og:description', content: '24 check locations on six continents. Cities take turns, failures are confirmed, and a blocked city is not an outage.' },
      { property: 'og:image', content: 'https://uptrack.app/og/blog-22-check-locations.png' },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: POST_URL },
      { property: 'article:published_time', content: '2026-10-11' },
    ],
    links: [{ rel: 'canonical', href: POST_URL }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: TITLE,
          description: DESCRIPTION,
          datePublished: '2026-10-11',
          dateModified: '2026-10-11',
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: { '@type': 'Organization', name: 'Uptrack', url: 'https://uptrack.app', logo: { '@type': 'ImageObject', url: 'https://uptrack.app/uptrack-logo-header.png' } },
          mainEntityOfPage: POST_URL,
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQ.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
        }),
      },
    ],
  }),
})

const p = 'mb-4 text-lg leading-[1.7] text-body'
const link = 'text-primary hover:underline'

const CONFIRMATION: { tool: string; how: string }[] = [
  { tool: 'UptimeRobot', how: 'Re-checks a failure, from the same region.' },
  { tool: 'Site24x7', how: 'Re-checks a failure; if any location sees the site up, the alert is cancelled.' },
  { tool: 'Datadog Synthetics', how: 'Alerts when N of M locations fail, optionally for a minimum time.' },
  { tool: 'Uptrack', how: 'Re-checks from the same city at once, needs 2–5 consecutive failures, and several locations must confirm.' },
]

const WHAT_RUNS_WHERE: { kind: string; where: string }[] = [
  { kind: 'HTTP and HTTPS', where: 'All 24 locations' },
  { kind: 'Keyword and response-body checks', where: 'All 24 locations' },
  { kind: 'Ping (ICMP), TCP port', where: 'Nuremberg, Hyderabad' },
  { kind: 'DNS, SSL certificate expiry', where: 'Nuremberg, Hyderabad' },
]

function CheckLocationsPage() {
  return (
    <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
      <p className="mb-3 text-sm text-muted-foreground">October 11, 2026</p>
      <h1 className="font-sans mb-6 text-4xl font-semibold">Checking your site from 24 locations without 24 times the false alarms</h1>
      {MONITOR_LOCATIONS.status && (
        <p className="mb-6 rounded-lg border border-border bg-muted p-4 text-lg leading-[1.7] text-body">{MONITOR_LOCATIONS.status}</p>
      )}

      <section className="mb-12">
        <p className={p}>
          A check from one place proves one thing: that place can reach your site. Your users sit in São Paulo, Jakarta and Johannesburg too.
          A CDN edge can fail in one city. A routing problem can cut off one region. A firewall rule can block a whole country by mistake.
        </p>
        <p className={p}>
          Uptrack used to check from three regions: Europe, the US and India. That caught a lot, but not a broken edge in Sydney or a
          bad route into Brazil. Today HTTP and keyword monitors run from <strong>24 locations on six continents</strong>.
        </p>
        <p className={p}>
          More locations bring a risk: more places where one bad network hop looks like an outage. This post shows how we add the
          coverage without adding noise.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Where we check from</h2>
        <div className="space-y-4">
          {CONTINENTS.map((continent) => (
            <div key={continent}>
              <h3 className="font-sans mb-2 text-lg font-semibold">{continent}</h3>
              <ul className="flex flex-wrap gap-x-5 gap-y-1 text-lg leading-[1.7] text-body">
                {LOCATIONS.filter((l) => l.continent === continent).map((l) => (
                  <li key={l.code}>
                    <span aria-hidden="true">{l.flag}</span> {l.city}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-4 text-lg leading-[1.7] text-body">
          The list shows the 24 Cloudflare cities and the 2 core probes: Nuremberg and Hyderabad. The core probes run every monitor type. The 24 cities run HTTP and keyword checks.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">More locations, not more noise</h2>
        <p className={p}>
          The 24 locations form 12 areas. Each area is two nearby Cloudflare cities: London and Paris, Singapore and Jakarta, Sydney and Melbourne, and so on.
          In each check interval one city per area checks your site, and the two cities take turns.
        </p>
        <p className={p}>
          So a 30-second monitor is checked from every area every 30 seconds, and from every city once a minute. Your site gets one request per
          area per interval, not one per city. The core probes check on every interval.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">How a failure gets confirmed</h2>
        <ol className="mb-4 list-decimal space-y-2 pl-6 text-lg leading-[1.7] text-body">
          <li><strong>Same-city re-check.</strong> When one of the 24 cities sees a failure, it checks again at once. A one-off network blip clears here.</li>
          <li><strong>Consecutive failures.</strong> The failure must repeat for your number of consecutive checks, from 2 to 5.</li>
          <li><strong>Several locations.</strong> Several locations must see the failure before we alert you. One unlucky city never pages you.</li>
        </ol>
        <p className={p}>
          The idea is older than the 24 locations. <Link to="/blog/why-alert-confirmation" className={link}>Why alert confirmation matters</Link> explains
          the consecutive-failure rule, and <Link to="/blog/multi-region-consensus" className={link}>how we built multi-region consensus</Link> tells the
          story of the first three regions.
        </p>
        <p className={p}>Other tools solve the same problem in different ways (from their public docs, October 2026):</p>
        <div className="mb-4 overflow-x-auto" tabIndex={0} role="region" aria-label="How tools confirm a failure">
          <table className="w-full text-sm">
            <thead className="hidden md:table-header-group">
              <tr className="border-b">
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Tool</th>
                <th className="pb-3 text-left font-medium text-muted-foreground">How a failure is confirmed</th>
              </tr>
            </thead>
            <tbody>
              {CONFIRMATION.map((row) => (
                <tr key={row.tool} className="block border-b py-3 md:table-row md:py-0 md:align-top">
                  <td className="block py-1 font-medium text-foreground md:table-cell md:py-3 md:pr-4">{row.tool}</td>
                  <td className="block py-1 text-body md:table-cell md:py-3">{row.how}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">A blocked city is not an outage</h2>
        <p className={p}>
          More locations also meet more bot protection. Some sites challenge or rate-limit traffic from certain networks, and a check
          from that city gets an HTTP 403 or 429 while every other location sees the site up.
        </p>
        <p className={p}>
          Uptrack does not call that an outage. When only one city is refused and the others see your site up, that city is marked as
          blocked for your site. The other city of the same area takes its turns, so the area stays covered.
          A real outage looks different: errors and timeouts from several places at once.
        </p>
        <p className={p}>
          Failures caused by bot protection also say so. Instead of a bare “HTTP 403”, the message names the protection that answered.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">What runs where</h2>
        <div className="mb-4 overflow-x-auto" tabIndex={0} role="region" aria-label="Monitor types and locations">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Monitor type</th>
                <th className="pb-3 text-left font-medium text-muted-foreground">Locations</th>
              </tr>
            </thead>
            <tbody>
              {WHAT_RUNS_WHERE.map((row) => (
                <tr key={row.kind} className="border-b">
                  <td className="py-3 pr-4 text-foreground">{row.kind}</td>
                  <td className="py-3 text-body">{row.where}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={p}>
          Pro includes 6 areas that you pick. Team and Business include all 24. The full plan comparison is on the{' '}
          <Link to="/pricing" className={link}>pricing page</Link>.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Try it on any site</h2>
        <p className={p}>
          The <Link to="/tools/is-website-down" className={link}>Is this website down?</Link> tool checks any URL from all
          24 locations and groups the answers by continent. No account needed.
        </p>
        <p className={p}>
          If you use Claude, the same check works from a chat: ask “is example.com down?” and the{' '}
          <Link to="/blog/uptrack-in-claude-connector-directory" className={link}>Uptrack connector</Link> answers with every location.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">Not for you if</h2>
        <ul className="list-disc space-y-2 pl-6 text-lg leading-[1.7] text-body">
          <li>You need ping, DNS or TCP checks from 24 locations. Those run from the 2 core locations.</li>
          <li>You need a traceroute or a full timing breakdown per city. The Cloudflare cities report total time and HTTP status.</li>
          <li>You want 24 locations on a free plan. The free plan checks from one location, Nuremberg. Pro picks 6 of the 12 areas from $3/mo.</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="font-sans mb-4 text-2xl font-semibold">FAQ</h2>
        {FAQ.map((f) => (
          <div key={f.q} className="mb-6">
            <h3 className="font-sans mb-2 text-lg font-semibold">{f.q}</h3>
            <p className="text-lg leading-[1.7] text-body">{f.a}</p>
          </div>
        ))}
      </section>

      <div className="rounded-lg border border-border bg-muted p-8 text-center">
        <h2 className="font-sans mb-3 text-xl font-semibold">Monitor from 24 locations</h2>
        <p className="mb-6 text-lg leading-[1.7] text-body">
          Pro has 50 monitors at 30-second checks, from $3/mo. If it is not right for you, the{' '}
          <Link to="/refund" className={link}>30-day money-back guarantee</Link> applies.
        </p>
        <Button asChild size="lg">
          <Link to="/pricing">
            See plans <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
      </div>
    </article>
  )
}
